/**
 * Singleton loader for the DPLE light editor SDK.
 *
 * The SDK script exposes a global `window.lightEditorSDK` once executed.
 * Multiple call sites (apps/configurator/index.js for the prefetch on URL
 * detection, DpleWizardProvider for the awaited init) can call ensureSdkLoaded
 * with the same URL — only one <script> tag ever lands in the DOM, and all
 * callers share the same load Promise.
 *
 * Returns a Promise<void> that resolves when window.lightEditorSDK is
 * available. Rejects when the script load errors out so callers can render
 * an error state.
 */

let loadPromise = null;

export const ensureSdkLoaded = (sdkUrl) => {
  if (window.lightEditorSDK) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.defer = true;

    // A script element fires `load` and `error` once each. Leaving a failed
    // one in the document would strand the next attempt: it would find the
    // tag, treat it as one already in flight, and wait for an event that has
    // already been and gone. Discard the element along with the promise so a
    // later attempt gets a fresh element and a fresh request. Note that where
    // the script ran but published no global, that retry re-executes it —
    // preferable to never recovering, but not free.
    const abandon = () => {
      cleanup();
      loadPromise = null;
      script.remove();
    };

    const onLoad = () => {
      // The SDK assigns window.lightEditorSDK during script execution, so by
      // the time `load` fires the global is ready.
      if (window.lightEditorSDK) {
        cleanup();
        resolve();
        return;
      }
      abandon();
      reject(new Error('SDK script loaded but window.lightEditorSDK is undefined'));
    };
    const onError = () => {
      abandon();
      reject(new Error(`Failed to load DPLE SDK from ${sdkUrl}`));
    };
    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    document.head.appendChild(script);
  });

  return loadPromise;
};
